import { format, parseISO } from "date-fns";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { LanguageToggle } from "@/components/layout/LanguageToggle";
import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/features/auth/AuthProvider";
import { TIER_VALUES, tierLabelKey } from "@/features/admin/tiers";
import { useHistoryStorage } from "@/services";
import type { TierChangeRequest, UserProfile } from "@/types";

/** Password minimum length — mirrors the auth flow (Supabase default is 6). */
const MIN_PASSWORD_LENGTH = 8;
const MAX_NOTE_LENGTH = 1000;

/**
 * SPEC-SETTINGS — "Ayarlar" page.
 *
 * Two sections:
 *   a. Account — email, current tier badge, role, member-since, an
 *      editable full-name field, a change-password sub-form, and a
 *      Theme & Language row that reuses the existing TopNav toggles.
 *   b. Tier change request — shows the current tier; a pending request
 *      can be cancelled; otherwise a request form (Select of requestable
 *      tiers excluding the current tier and 'unlimited' + optional note);
 *      plus a history list of past requests.
 *
 * All RPC error sentinels surfaced by SupabaseHistoryStorage are mapped
 * to friendly i18n messages.
 */
export function SettingsPage() {
  const { t } = useTranslation();
  const storage = useHistoryStorage();

  const [profile, setProfile] = React.useState<UserProfile | null>(null);
  const [profileError, setProfileError] = React.useState(false);
  const [requests, setRequests] = React.useState<TierChangeRequest[] | null>(
    null,
  );

  const loadProfile = React.useCallback(async (): Promise<void> => {
    try {
      const p = await storage.getMyProfile();
      setProfile(p);
    } catch (err) {
      console.error("[settings] getMyProfile failed", err);
      setProfileError(true);
      toast.error(t("page.settings.account.loadError"));
    }
  }, [storage, t]);

  const loadRequests = React.useCallback(async (): Promise<void> => {
    try {
      const rows = await storage.listMyTierRequests();
      setRequests(rows);
    } catch (err) {
      console.error("[settings] listMyTierRequests failed", err);
      toast.error(t("page.settings.tier.loadError"));
      setRequests([]);
    }
  }, [storage, t]);

  React.useEffect(() => {
    void loadProfile();
    void loadRequests();
  }, [loadProfile, loadRequests]);

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("page.settings.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("page.settings.subtitle")}
        </p>
      </div>

      <AccountSection
        profile={profile}
        profileError={profileError}
        onProfileUpdated={(p) => setProfile(p)}
      />

      <TierRequestSection
        profile={profile}
        requests={requests}
        onRefresh={() => {
          void loadRequests();
          void loadProfile();
        }}
      />
    </div>
  );
}

// ─── Account section ────────────────────────────────────────────────────────

interface AccountSectionProps {
  profile: UserProfile | null;
  profileError: boolean;
  onProfileUpdated: (profile: UserProfile) => void;
}

function AccountSection({
  profile,
  profileError,
  onProfileUpdated,
}: AccountSectionProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const storage = useHistoryStorage();

  const [fullName, setFullName] = React.useState("");
  const [savingName, setSavingName] = React.useState(false);

  // Seed the editable name once the profile resolves.
  React.useEffect(() => {
    if (profile) setFullName(profile.fullName ?? "");
  }, [profile]);

  const email = profile?.email ?? user?.email ?? null;

  async function handleSaveName(): Promise<void> {
    const trimmed = fullName.trim();
    setSavingName(true);
    try {
      await storage.updateMyFullName(trimmed);
      if (profile) onProfileUpdated({ ...profile, fullName: trimmed });
      toast.success(t("page.settings.account.nameSaved"));
    } catch (err) {
      console.error("[settings] updateMyFullName failed", err);
      toast.error(t("page.settings.account.nameError"));
    } finally {
      setSavingName(false);
    }
  }

  return (
    <Card data-testid="settings-account-card">
      <CardHeader>
        <CardTitle>{t("page.settings.account.title")}</CardTitle>
        <CardDescription>
          {t("page.settings.account.description")}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {profileError ? (
          <p className="text-sm text-destructive">
            {t("page.settings.account.loadError")}
          </p>
        ) : null}

        {/* Identity grid */}
        <div className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2">
          <span className="text-xs text-muted-foreground">
            {t("page.settings.account.email")}
          </span>
          <span className="truncate text-sm" data-testid="settings-email">
            {email ?? "—"}
          </span>

          <span className="text-xs text-muted-foreground">
            {t("page.settings.account.tier")}
          </span>
          <span>
            {profile ? (
              <Badge variant="secondary" data-testid="settings-tier-badge">
                {t(tierLabelKey(profile.tier) as never) as string}
              </Badge>
            ) : (
              "—"
            )}
          </span>

          <span className="text-xs text-muted-foreground">
            {t("page.settings.account.role")}
          </span>
          <span className="text-sm">
            {profile
              ? profile.role === "admin"
                ? t("page.settings.account.roleAdmin")
                : t("page.settings.account.roleUser")
              : "—"}
          </span>

          <span className="text-xs text-muted-foreground">
            {t("page.settings.account.memberSince")}
          </span>
          <span className="text-sm">
            {profile ? safeFormat(profile.createdAt, "dd.MM.yyyy") : "—"}
          </span>
        </div>

        {/* Full name editor */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="settings-full-name">
            {t("page.settings.account.fullName")}
          </Label>
          <div className="flex items-center gap-2">
            <Input
              id="settings-full-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              disabled={!profile || savingName}
              placeholder={t("page.settings.account.fullNamePlaceholder")}
              data-testid="settings-full-name-input"
            />
            <Button
              onClick={() => void handleSaveName()}
              disabled={!profile || savingName}
              data-testid="settings-save-name"
            >
              {t("common.save")}
            </Button>
          </div>
        </div>

        <div className="h-px bg-border" />

        <ChangePasswordForm />

        <div className="h-px bg-border" />

        {/* Theme & language — reuse the TopNav toggles directly. */}
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-medium">
            {t("page.settings.account.themeAndLanguage")}
          </p>
          <div className="flex items-center gap-2">
            <LanguageToggle />
            <ThemeToggle />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Change password sub-form ───────────────────────────────────────────────

function ChangePasswordForm() {
  const { t } = useTranslation();
  const { updatePassword } = useAuth();

  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (password.length < MIN_PASSWORD_LENGTH) {
      toast.error(
        t("page.settings.password.tooShort", { min: MIN_PASSWORD_LENGTH }),
      );
      return;
    }
    if (password !== confirm) {
      toast.error(t("page.settings.password.mismatch"));
      return;
    }
    setSaving(true);
    try {
      await updatePassword(password);
      setPassword("");
      setConfirm("");
      toast.success(t("page.settings.password.success"));
    } catch (err) {
      console.error("[settings] updatePassword failed", err);
      toast.error(t("page.settings.password.error"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => void handleSubmit(e)}
      data-testid="settings-password-form"
    >
      <p className="text-sm font-medium">{t("page.settings.password.title")}</p>
      <div className="flex flex-col gap-2">
        <Label htmlFor="settings-new-password">
          {t("page.settings.password.newPassword")}
        </Label>
        <Input
          id="settings-new-password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={saving}
          data-testid="settings-new-password"
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="settings-confirm-password">
          {t("page.settings.password.confirmPassword")}
        </Label>
        <Input
          id="settings-confirm-password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          disabled={saving}
          data-testid="settings-confirm-password"
        />
      </div>
      <p className="text-xs text-muted-foreground">
        {t("page.settings.password.hint", { min: MIN_PASSWORD_LENGTH })}
      </p>
      <div>
        <Button
          type="submit"
          disabled={saving}
          data-testid="settings-change-password"
        >
          {t("page.settings.password.submit")}
        </Button>
      </div>
    </form>
  );
}

// ─── Tier change request section ────────────────────────────────────────────

interface TierRequestSectionProps {
  profile: UserProfile | null;
  requests: TierChangeRequest[] | null;
  onRefresh: () => void;
}

function TierRequestSection({
  profile,
  requests,
  onRefresh,
}: TierRequestSectionProps) {
  const { t } = useTranslation();
  const storage = useHistoryStorage();

  const [requestedTier, setRequestedTier] = React.useState("");
  const [note, setNote] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [cancelling, setCancelling] = React.useState(false);

  const pending = React.useMemo(
    () => requests?.find((r) => r.status === "pending") ?? null,
    [requests],
  );

  // Show the full requestable ladder — every tier EXCEPT 'unlimited'
  // (admin-only). The user's current tier is rendered but disabled (with a
  // "(current plan)" marker) so they see the whole ladder without being able
  // to submit a no-op self-request.
  const options = React.useMemo(
    () => TIER_VALUES.filter((tier) => tier !== "unlimited"),
    [],
  );

  async function handleSubmit(): Promise<void> {
    if (!requestedTier) return;
    setSubmitting(true);
    try {
      await storage.requestTierChange(requestedTier, note.trim() || undefined);
      setRequestedTier("");
      setNote("");
      toast.success(t("page.settings.tier.submitSuccess"));
      onRefresh();
    } catch (err) {
      toast.error(t(tierRequestErrorKey((err as Error).message) as never) as string);
      console.error("[settings] requestTierChange failed", err);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCancel(): Promise<void> {
    if (!pending) return;
    setCancelling(true);
    try {
      await storage.cancelTierChange(pending.id);
      toast.success(t("page.settings.tier.cancelSuccess"));
      onRefresh();
    } catch (err) {
      toast.error(t(tierRequestErrorKey((err as Error).message) as never) as string);
      console.error("[settings] cancelTierChange failed", err);
    } finally {
      setCancelling(false);
    }
  }

  return (
    <Card data-testid="settings-tier-card">
      <CardHeader>
        <CardTitle>{t("page.settings.tier.title")}</CardTitle>
        <CardDescription>{t("page.settings.tier.description")}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">
            {t("page.settings.tier.currentTier")}
          </span>
          {profile ? (
            <Badge variant="secondary" data-testid="settings-tier-current">
              {t(tierLabelKey(profile.tier) as never) as string}
            </Badge>
          ) : (
            <span>—</span>
          )}
        </div>

        {pending ? (
          <div
            className="flex flex-col gap-3 rounded-lg border border-border p-3"
            data-testid="settings-tier-pending"
          >
            <p className="text-sm">
              {t("page.settings.tier.pendingNotice", {
                tier: t(tierLabelKey(pending.requestedTier) as never) as string,
              })}
            </p>
            {pending.note ? (
              <p className="text-xs text-muted-foreground">
                {t("page.settings.tier.yourNote")}: {pending.note}
              </p>
            ) : null}
            <div>
              <Button
                variant="outline"
                onClick={() => void handleCancel()}
                disabled={cancelling}
                data-testid="settings-tier-cancel"
              >
                {t("page.settings.tier.cancel")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2">
              <Label htmlFor="settings-tier-select">
                {t("page.settings.tier.requestedTier")}
              </Label>
              <Select
                value={requestedTier}
                onValueChange={setRequestedTier}
                disabled={!profile || submitting}
              >
                <SelectTrigger
                  id="settings-tier-select"
                  className="w-56"
                  data-testid="settings-tier-select"
                >
                  <SelectValue
                    placeholder={t("page.settings.tier.selectPlaceholder")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {options.map((opt) => {
                    const isCurrent = opt === profile?.tier;
                    return (
                      <SelectItem key={opt} value={opt} disabled={isCurrent}>
                        {t(tierLabelKey(opt) as never) as string}
                        {isCurrent
                          ? ` ${t("page.settings.tier.currentMarker")}`
                          : ""}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="settings-tier-note">
                {t("page.settings.tier.note")}
              </Label>
              <Textarea
                id="settings-tier-note"
                value={note}
                maxLength={MAX_NOTE_LENGTH}
                onChange={(e) => setNote(e.target.value)}
                disabled={!profile || submitting}
                placeholder={t("page.settings.tier.notePlaceholder")}
                data-testid="settings-tier-note"
              />
              <span className="text-right text-xs text-muted-foreground">
                {note.length}/{MAX_NOTE_LENGTH}
              </span>
            </div>
            <div>
              <Button
                onClick={() => void handleSubmit()}
                disabled={!profile || submitting || !requestedTier}
                data-testid="settings-tier-submit"
              >
                {t("page.settings.tier.submit")}
              </Button>
            </div>
          </div>
        )}

        {/* History */}
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium">
            {t("page.settings.tier.historyTitle")}
          </p>
          {requests === null ? (
            <div className="h-12 animate-pulse rounded bg-muted/40" />
          ) : requests.length === 0 ? (
            <p className="text-sm italic text-muted-foreground">
              {t("page.settings.tier.historyEmpty")}
            </p>
          ) : (
            <ul className="flex flex-col gap-2" data-testid="settings-tier-history">
              {requests.map((r) => (
                <li
                  key={r.id}
                  className="rounded-lg border border-border p-3 text-sm"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span>
                      {t(tierLabelKey(r.currentTier) as never) as string} →{" "}
                      {t(tierLabelKey(r.requestedTier) as never) as string}
                    </span>
                    <TierRequestStatusBadge status={r.status} />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {safeFormat(r.createdAt, "dd.MM.yyyy HH:mm")}
                    {r.reviewedAt
                      ? ` · ${t("page.settings.tier.reviewedAt", {
                          date: safeFormat(r.reviewedAt, "dd.MM.yyyy HH:mm"),
                        })}`
                      : ""}
                  </p>
                  {r.reviewNote ? (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t("page.settings.tier.reviewNote")}: {r.reviewNote}
                    </p>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Map a tier-request RPC error sentinel to its i18n key. */
export function tierRequestErrorKey(code: string): string {
  switch (code) {
    case "TIERREQ_AUTH_REQUIRED":
      return "page.settings.tier.error.authRequired";
    case "TIERREQ_INVALID_TIER":
      return "page.settings.tier.error.invalidTier";
    case "TIERREQ_NOTE_TOO_LONG":
      return "page.settings.tier.error.noteTooLong";
    case "TIERREQ_PROFILE_NOT_FOUND":
      return "page.settings.tier.error.profileNotFound";
    case "TIERREQ_ALREADY_ON_TIER":
      return "page.settings.tier.error.alreadyOnTier";
    case "TIERREQ_PENDING_EXISTS":
      return "page.settings.tier.error.pendingExists";
    case "TIERREQ_NOT_FOUND":
      return "page.settings.tier.error.notFound";
    case "TIERREQ_NOT_OWNER":
      return "page.settings.tier.error.notOwner";
    case "TIERREQ_NOT_PENDING":
      return "page.settings.tier.error.notPending";
    case "TIERREQ_NOT_ADMIN":
      return "page.settings.tier.error.notAdmin";
    default:
      return "page.settings.tier.error.generic";
  }
}

function TierRequestStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const variant =
    status === "approved"
      ? "default"
      : status === "rejected"
        ? "destructive"
        : status === "cancelled"
          ? "outline"
          : "secondary";
  return (
    <Badge variant={variant} data-testid={`settings-tier-status-${status}`}>
      {t(`page.settings.tier.status.${status}` as never) as string}
    </Badge>
  );
}

function safeFormat(iso: string, fmt: string): string {
  try {
    const date = parseISO(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return format(date, fmt);
  } catch {
    return iso;
  }
}
